import { useEffect, useId, useMemo, useState } from "react";
import type { PersonalizedFertilizerPdpResponse } from "@halm-club/halm-shared";
import { LoadingSpinner } from "../../../../shared/pdp/components/LoadingSpinner";
import { Modal } from "../../../../shared/pdp/components/Modal";
import { getActiveFertilizerSeason } from "../../../../shared/pdp/utils/fertilizerCartProperties";
import { formatMoney } from "../../../../shared/pdp/utils/pricing";
import type { PersonalizedFertilizerPdpConfig } from "../../types";
import type {
  FertilizerPurchaseOption,
  FertilizerVariant,
} from "../../purchaseOptions";
import "./FertilizerPurchaseModal.css";

type ReviewContent = PersonalizedFertilizerPdpResponse["reviewContent"];
type Season = PersonalizedFertilizerPdpConfig["currentSeason"];

type FertilizerPurchaseModalProps = {
  config: PersonalizedFertilizerPdpConfig;
  isAddingToCart: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (purchaseOption: FertilizerPurchaseOption) => void;
  reviewContent?: ReviewContent;
  soilAnalysisPrice: number;
  soilAnalysisType: "AI" | "LAB";
  statusMessage: string;
  variant: FertilizerVariant;
};

const SEASONS: Record<
  Season,
  { description: string; fallbackDate: string; label: string; next: Season }
> = {
  spring: {
    description:
      "Bringt den Rasen nach dem Winter auf Spur und beugt Unkraut und Moos vor.",
    fallbackDate: "1. März",
    label: "Frühjahr",
    next: "summer",
  },
  summer: {
    description:
      "Stärkt deinen Rasen bei Hitze und hilft gegen Trockenschäden.",
    fallbackDate: "1. Juni",
    label: "Sommer",
    next: "fall",
  },
  fall: {
    description:
      "Stärkt deinen Rasen nach dem Sommer und bereitet ihn auf den Winter vor.",
    fallbackDate: "1. Sept",
    label: "Herbst",
    next: "spring",
  },
};

const getSeasonKey = (season: ReturnType<typeof getActiveFertilizerSeason>) =>
  season.toLowerCase() as Season;

const getSeasonSequence = (start: Season) => {
  const second = SEASONS[start].next;
  return [start, second, SEASONS[second].next];
};

export function FertilizerPurchaseModal({
  config,
  isAddingToCart,
  isOpen,
  onClose,
  onConfirm,
  reviewContent,
  soilAnalysisPrice,
  soilAnalysisType,
  statusMessage,
  variant,
}: FertilizerPurchaseModalProps) {
  const headingId = useId();
  const [selectedPurchaseType, setSelectedPurchaseType] =
    useState<FertilizerPurchaseOption["purchaseType"]>("SUBSCRIPTION");
  const [selectedSeason, setSelectedSeason] = useState<Season>("spring");
  const [isLearnMoreExpanded, setIsLearnMoreExpanded] = useState(false);
  const subscriptionOption = variant.purchaseOptions.find(
    (option) => option.purchaseType === "SUBSCRIPTION",
  );
  const onetimeOption = variant.purchaseOptions.find(
    (option) => option.purchaseType === "ONETIME",
  );
  const selectedPurchaseOption = variant.purchaseOptions.find(
    (option) => option.purchaseType === selectedPurchaseType,
  );
  const activeSeason = useMemo(
    () => getSeasonKey(getActiveFertilizerSeason(config.currentSeason)),
    [config.currentSeason],
  );
  const seasonSequence = useMemo(
    () => getSeasonSequence(activeSeason),
    [activeSeason],
  );
  const totalPrice =
    (selectedPurchaseOption?.priceCents ?? 0) + soilAnalysisPrice;
  const rating = reviewContent?.rating;
  const numberOfReviews = reviewContent?.numberOfReviews;
  const hasRating =
    typeof rating === "number" && typeof numberOfReviews === "number";

  useEffect(() => {
    if (isOpen) {
      setSelectedPurchaseType("SUBSCRIPTION");
      setSelectedSeason(activeSeason);
    }
  }, [activeSeason, isOpen]);

  return (
    <Modal
      className="personalized-fertilizer-pdp__purchase-modal"
      dataTest="fertilizer-purchase-modal"
      dismissalDisabled={isAddingToCart}
      isOpen={isOpen}
      labelledBy={headingId}
      onClose={onClose}
      overlayClassName="personalized-fertilizer-pdp__purchase-modal-overlay"
    >
      <div className="personalized-fertilizer-pdp__purchase-modal-handle-wrapper">
        <span className="personalized-fertilizer-pdp__purchase-modal-handle" />
      </div>

      <div className="personalized-fertilizer-pdp__purchase-modal-content">
        <header className="personalized-fertilizer-pdp__purchase-modal-header">
          <h2
            className="personalized-fertilizer-pdp__purchase-modal-heading"
            id={headingId}
          >
            Wie möchtest du deinen Rasen versorgen?
          </h2>
          <button
            aria-label="Dialog schließen"
            className="personalized-fertilizer-pdp__purchase-modal-close"
            disabled={isAddingToCart}
            onClick={onClose}
            type="button"
          >
            <span aria-hidden="true" className="material-symbols-rounded">
              close
            </span>
          </button>
        </header>

        <div
          aria-label="Liefermodell auswählen"
          className="personalized-fertilizer-pdp__purchase-modal-options"
          role="radiogroup"
        >
          {subscriptionOption ? (
            <label
              className={`personalized-fertilizer-pdp__purchase-modal-option personalized-fertilizer-pdp__purchase-modal-option--subscription${
                selectedPurchaseType === "SUBSCRIPTION" ? " is-selected" : ""
              }`}
              data-test="fertilizer-purchase-option-subscription"
            >
              <span className="personalized-fertilizer-pdp__purchase-modal-recommended">
                Empfohlen
              </span>
              <input
                checked={selectedPurchaseType === "SUBSCRIPTION"}
                className="visually-hidden"
                name="fertilizer-purchase-type"
                onChange={() => setSelectedPurchaseType("SUBSCRIPTION")}
                type="radio"
                value="SUBSCRIPTION"
              />
              <span
                aria-hidden="true"
                className="personalized-fertilizer-pdp__purchase-modal-radio"
              />
              <div className="personalized-fertilizer-pdp__purchase-modal-option-body">
                <div className="personalized-fertilizer-pdp__purchase-modal-option-header">
                  <strong>Immer richtig: Dünger im Abo</strong>
                  <span className="personalized-fertilizer-pdp__purchase-modal-option-prices">
                    {subscriptionOption.compareAtPriceCents &&
                    subscriptionOption.compareAtPriceCents >
                      subscriptionOption.priceCents ? (
                      <s>
                        {formatMoney(subscriptionOption.compareAtPriceCents)}
                      </s>
                    ) : null}
                    <b>{formatMoney(subscriptionOption.priceCents)}</b>
                    <small>pro Lieferung</small>
                  </span>
                </div>

                <div
                  aria-label="Informationen zu den Jahreszeiten"
                  className="personalized-fertilizer-pdp__purchase-modal-seasons"
                  role="group"
                >
                  {seasonSequence.map((season, index) => (
                    <div
                      className="personalized-fertilizer-pdp__purchase-modal-season-wrap"
                      key={season}
                    >
                      {index > 0 ? (
                        <span
                          aria-hidden="true"
                          className="material-symbols-rounded personalized-fertilizer-pdp__purchase-modal-season-arrow"
                        >
                          chevron_right
                        </span>
                      ) : null}
                      <button
                        aria-pressed={selectedSeason === season}
                        className={`personalized-fertilizer-pdp__purchase-modal-season${
                          selectedSeason === season ? " is-selected" : ""
                        }`}
                        data-test={`fertilizer-season-${season}`}
                        onClick={(event) => {
                          event.preventDefault();
                          setSelectedSeason(season);
                        }}
                        type="button"
                      >
                        <strong>{SEASONS[season].label}</strong>
                        {index === 0 ? (
                          <span>START</span>
                        ) : (
                          <small>
                            {config.seasonShippingDates[season] ||
                              SEASONS[season].fallbackDate}
                          </small>
                        )}
                      </button>
                    </div>
                  ))}
                </div>

                <p className="personalized-fertilizer-pdp__purchase-modal-season-copy">
                  <strong>{SEASONS[selectedSeason].label}:</strong>{" "}
                  {SEASONS[selectedSeason].description}
                </p>
                <div className="personalized-fertilizer-pdp__purchase-modal-subscription-copy">
                  <p>
                    ✨ Saisonal abgestimmt und geliefert, wenn dein Rasen es
                    braucht. Mindestverpflichtung von drei Lieferungen, danach
                    jederzeit kündbar.{" "}
                    {!isLearnMoreExpanded ? (
                      <button
                        onClick={(event) => {
                          event.preventDefault();
                          setIsLearnMoreExpanded(true);
                        }}
                        type="button"
                      >
                        Mehr erfahren
                      </button>
                    ) : null}
                  </p>
                  {isLearnMoreExpanded ? (
                    <p>
                      Jeder Dünger wird zum Zeitpunkt des Versands mit{" "}
                      <strong>
                        {formatMoney(subscriptionOption.priceCents)}
                      </strong>{" "}
                      berechnet.
                    </p>
                  ) : null}
                </div>
              </div>
            </label>
          ) : null}

          {onetimeOption ? (
            <label
              className={`personalized-fertilizer-pdp__purchase-modal-option${
                selectedPurchaseType === "ONETIME" ? " is-selected" : ""
              }`}
              data-test="fertilizer-purchase-option-onetime"
            >
              <input
                checked={selectedPurchaseType === "ONETIME"}
                className="visually-hidden"
                name="fertilizer-purchase-type"
                onChange={() => setSelectedPurchaseType("ONETIME")}
                type="radio"
                value="ONETIME"
              />
              <span
                aria-hidden="true"
                className="personalized-fertilizer-pdp__purchase-modal-radio"
              />
              <div className="personalized-fertilizer-pdp__purchase-modal-option-header personalized-fertilizer-pdp__purchase-modal-option-header--onetime">
                <span>
                  <strong>Nur {SEASONS[activeSeason].label}-Lieferung</strong>
                  <small>Einmalig - ohne automatische Folgeversorgung</small>
                </span>
                <b>{formatMoney(onetimeOption.priceCents)}</b>
              </div>
            </label>
          ) : null}
        </div>

        <div className="personalized-fertilizer-pdp__purchase-modal-footer">
          {hasRating ? (
            <div
              aria-label={`Hervorragend, ${rating} von 5 Sternen, ${numberOfReviews} Bewertungen`}
              className="personalized-fertilizer-pdp__purchase-modal-rating"
            >
              <strong>Hervorragend</strong>
              <span aria-hidden="true">
                {Array.from({ length: 5 }, (_, index) => (
                  <span
                    className="material-symbols-rounded filled-icon"
                    key={index}
                  >
                    star
                  </span>
                ))}
              </span>
              <span>
                <strong>{rating}</strong> (+{numberOfReviews})
              </span>
            </div>
          ) : null}
          <button
            className="personalized-fertilizer-pdp__purchase-modal-confirm"
            data-test="fertilizer-purchase-confirm"
            disabled={isAddingToCart || !selectedPurchaseOption}
            onClick={() => {
              if (selectedPurchaseOption) onConfirm(selectedPurchaseOption);
            }}
            type="button"
          >
            {isAddingToCart ? <LoadingSpinner /> : null}
            <span>
              Weiter mit{" "}
              {selectedPurchaseOption?.purchaseType === "SUBSCRIPTION"
                ? "Dünger im Abo"
                : `${SEASONS[activeSeason].label}-Lieferung`}
            </span>
          </button>
          <p
            className="personalized-fertilizer-pdp__purchase-modal-total"
            data-test="fertilizer-purchase-total"
          >
            Heute: <strong>{formatMoney(totalPrice)}</strong> inkl.{" "}
            {soilAnalysisType === "LAB" ? "Bodenanalyse" : "KI-Analyse"}
          </p>
          {statusMessage ? (
            <p
              className="personalized-fertilizer-pdp__purchase-modal-error"
              role="alert"
            >
              {statusMessage}
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
